import * as React from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

type ComboboxOption = string | { value: string; label: string };

interface ComboboxProps {
    options: ComboboxOption[];
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    className?: string;
    contentClassName?: string;
    contentStyle?: React.CSSProperties;
    listClassName?: string;
    listStyle?: React.CSSProperties;
    scrollableOptions?: boolean;
    emptyText?: string;
    'aria-label'?: string;
}

export function Combobox({
    options = [],
    value,
    onChange,
    placeholder = 'Select option...',
    emptyText = 'No results found.',
    className,
    contentClassName,
    contentStyle,
    listClassName,
    listStyle,
    scrollableOptions = false,
    'aria-label': ariaLabel,
}: ComboboxProps) {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState('');
    const normalizedOptions = options.map((option) =>
        typeof option === 'string' ? { value: option, label: option } : option
    );
    const selectedOption = normalizedOptions.find(
        (option) => option.value === value
    );
    const filteredOptions = normalizedOptions.filter((option) => {
        const search = query.trim().toLowerCase();

        if (!search) {
            return true;
        }

        return `${option.label} ${option.value}`.toLowerCase().includes(search);
    });

    const handleSelect = (currentValue: string) => {
        setOpen(false);
        setQuery('');
        onChange?.(currentValue);
    };

    if (scrollableOptions) {
        return (
            <Popover
                open={open}
                onOpenChange={(nextOpen) => {
                    setOpen(nextOpen);
                    if (!nextOpen) {
                        setQuery('');
                    }
                }}
            >
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        aria-label={ariaLabel}
                        className={cn(
                            'w-full justify-between font-normal shadow-sm hover:bg-transparent focus:ring-1 focus:ring-ring',
                            !value && 'text-foreground',
                            className
                        )}
                    >
                        {selectedOption?.label || placeholder}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>

                <PopoverContent
                    className={cn(
                        'z-[1000] w-[--radix-popover-trigger-width] overflow-hidden p-0',
                        contentClassName
                    )}
                    style={contentStyle}
                >
                    <div className="flex items-center border-b px-3">
                        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50 text-white" />
                        <input
                            className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm text-white outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                            placeholder={placeholder}
                            value={query}
                            autoFocus
                            onChange={(event) => setQuery(event.target.value)}
                        />
                    </div>
                    <div
                        className={cn(
                            'max-h-60 overflow-y-auto overflow-x-hidden p-1',
                            listClassName
                        )}
                        style={listStyle}
                        role="listbox"
                        onWheel={(event) => {
                            event.currentTarget.scrollTop += event.deltaY;
                            event.stopPropagation();
                        }}
                    >
                        {filteredOptions.length === 0 ? (
                            <div className="py-6 text-center text-sm">
                                {emptyText}
                            </div>
                        ) : (
                            filteredOptions.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    role="option"
                                    aria-selected={value === option.value}
                                    onClick={() => handleSelect(option.value)}
                                    className="relative flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:cursor-pointer hover:bg-accent hover:text-accent-foreground"
                                >
                                    <Check
                                        className={cn(
                                            'mr-2 h-4 w-4',
                                            value === option.value
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                        )}
                                    />
                                    {option.label}
                                </button>
                            ))
                        )}
                    </div>
                </PopoverContent>
            </Popover>
        );
    }

    return (
        <Popover
            open={open}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                if (!nextOpen) {
                    setQuery('');
                }
            }}
        >
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    aria-label={ariaLabel}
                    className={cn(
                        'w-full justify-between font-normal shadow-sm hover:bg-transparent focus:ring-1 focus:ring-ring',
                        !value && 'text-foreground',
                        className
                    )}
                >
                    {selectedOption?.label || placeholder}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className={cn(
                    'w-[--radix-popover-trigger-width] p-0',
                    contentClassName
                )}
                style={contentStyle}
            >
                <Command shouldFilter={false}>
                    <CommandInput
                        placeholder={placeholder}
                        value={query}
                        onValueChange={setQuery}
                    />
                    <CommandList className={listClassName} style={listStyle}>
                        <CommandEmpty>{emptyText}</CommandEmpty>
                        <CommandGroup>
                            {filteredOptions.map((option) => (
                                <CommandItem
                                    key={option.value}
                                    value={option.value}
                                    onSelect={handleSelect}
                                    className="hover:bg-accent hover:text-accent-foreground data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
                                >
                                    <Check
                                        className={cn(
                                            'mr-2 h-4 w-4',
                                            value === option.value
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                        )}
                                    />
                                    {option.label}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
